import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box
} from '@material-ui/core';
import * as Actions from '../../store/actions';
import * as OrganizationActions from 'app/main/organizations/store/actions';

/**
 * Move a cluster to another organization, or out to the platform level.
 *
 * A cluster's organization was fixed at creation, so a shared Heidi service that
 * happened to be created inside a tenant's organization was stuck there - and
 * that organization could not be deleted while it existed.
 *
 * Two things move with it and both are choices the operator has to make, so
 * they are on the form rather than implied: the OWNER (the current one is a
 * member of the organization being left and disappears with it) and the
 * provider CREDENTIAL (org-scoped too, and a blocker on deleting the old
 * organization if it stays behind).
 *
 * Platform super-admins only; the API enforces that independently.
 *
 * Feature: cluster-org-transfer
 */
const PLATFORM = '__platform__';

const TransferClusterOrganizationDialog = ({ cluster, onClose }) => {
  const dispatch = useDispatch();
  const organizations = useSelector(({ organizations: orgs }) => orgs?.organization?.list) || [];

  // A cluster already at the platform level has nowhere to go by default, so it
  // starts with nothing selected rather than on a destination it is already at.
  const [destination, setDestination] = useState(cluster.organizationId ? PLATFORM : '');
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState('');
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [moveCredential, setMoveCredential] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const currentOrgId = cluster.organizationId || null;
  const targetOrgId = destination === PLATFORM ? null : (destination || null);

  useEffect(() => {
    if (!organizations.length) dispatch(OrganizationActions.getOrganizations(false));
  }, [dispatch, organizations.length]);

  // Eligible owners are a property of the DESTINATION, not of the cluster, so
  // they are re-fetched every time the destination changes. Picking an owner
  // from the wrong side is the mistake this prevents.
  useEffect(() => {
    let cancelled = false;
    setLoadingOwners(true);
    setOwnerId('');
    dispatch(Actions.getClusterTransferOwners(cluster.id, targetOrgId))
      .then((list) => {
        if (cancelled) return;
        setOwners(list);
        // Keeping the current owner is valid when they already belong to the
        // destination; otherwise leave it unset so the operator must choose.
        const stillValid = list.find((u) => u.id === cluster.owner);
        setOwnerId(stillValid ? stillValid.id : '');
        setLoadingOwners(false);
      })
      .catch(() => {
        if (cancelled) return;
        setOwners([]);
        setLoadingOwners(false);
      });
    return () => { cancelled = true; };
  }, [dispatch, cluster.id, cluster.owner, targetOrgId]);

  const destinations = useMemo(
    () => organizations.filter((o) => o && o.id && o.id !== currentOrgId),
    [organizations, currentOrgId]
  );

  const currentOrgName = useMemo(() => {
    if (!currentOrgId) return 'the platform level';
    const org = organizations.find((o) => o.id === currentOrgId);
    return org ? org.name : cluster.organizationName || currentOrgId;
  }, [organizations, currentOrgId, cluster.organizationName]);

  const handleTransfer = async () => {
    setBusy(true);
    setError(null);
    try {
      const summary = await dispatch(Actions.transferClusterOrganization(cluster.id, {
        organizationId: targetOrgId,
        newOwnerId: ownerId || null,
        moveCredential
      }));
      setResult(summary);
    } catch (err) {
      // jwtService's axios interceptor rejects with the RESPONSE, not the axios
      // error, so err.data holds the body and err.response is undefined. The
      // reasons this endpoint refuses (busy cluster, credential still in use)
      // are the whole value of the message - losing them to a generic
      // "Transfer failed" leaves the operator with nothing to act on.
      const body = err.data || err.response?.data;
      setError(body?.message || err.message || 'Transfer failed');
    }
    setBusy(false);
  };

  if (result) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Cluster transferred</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <strong>{cluster.displayName || cluster.name}</strong> now belongs to{' '}
            <strong>{result.to ? (organizations.find((o) => o.id === result.to) || {}).name || result.to : 'the platform level'}</strong>,
            owned by <strong>{result.owner.name}</strong>.
          </Typography>
          {result.removedCollaborators.length > 0 && (
            <Typography variant="caption" color="textSecondary" component="div">
              Removed collaborators: {result.removedCollaborators.join(', ')}
            </Typography>
          )}
          {result.credential && (
            <Typography variant="caption" color="textSecondary" component="div">
              Credential "{result.credential.name}" moved with it.
            </Typography>
          )}
          {result.directory.moved && (
            <Typography variant="caption" color="textSecondary" component="div">
              Working directory moved to {result.directory.to}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={busy ? null : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Move cluster to another organization</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          <strong>{cluster.displayName || cluster.name}</strong> currently belongs to{' '}
          <strong>{currentOrgName}</strong>.
          {currentOrgId
            ? ' Moving it lets that organization be deleted without destroying the cluster.'
            : ' Moving it hands the cluster to an organization, which can then operate it.'}
        </Typography>

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">Destination</Typography>
          <Select
            fullWidth
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            displayEmpty
            disabled={busy}
          >
            <MenuItem value="" disabled>
              Pick a destination
            </MenuItem>
            <MenuItem value={PLATFORM} disabled={!currentOrgId}>
              Platform level (no organization)
            </MenuItem>
            {destinations.map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">Owner after the move</Typography>
          <Select
            fullWidth
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            displayEmpty
            disabled={busy || loadingOwners}
          >
            <MenuItem value="" disabled>
              {loadingOwners ? 'Loading...' : 'Pick an owner'}
            </MenuItem>
            {owners.map((u) => (
              <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
            ))}
          </Select>
          {!loadingOwners && owners.length === 0 && (
            <Typography variant="caption" color="error" component="div" style={{ marginTop: 6 }}>
              No users exist at the destination. Create one there first - a cluster with no
              owner on its own side of the boundary cannot be operated.
            </Typography>
          )}
        </Box>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={moveCredential}
                onChange={(e) => setMoveCredential(e.target.checked)}
                disabled={busy}
              />
            }
            label="Move the provider credential too"
          />
          <Typography variant="caption" color="textSecondary" component="div">
            Credentials are org-scoped. Left behind, the credential keeps the old
            organization undeletable and the cluster loses the account it deploys with.
            Refused automatically if another cluster still uses it.
          </Typography>
        </Box>

        {error && (
          <Box mt={2}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={handleTransfer}
          color="primary"
          variant="contained"
          disabled={busy || loadingOwners || !destination || !ownerId}
        >
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Move cluster'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferClusterOrganizationDialog;
